import { validateDateRange } from '~support-at-home/lib/validate';

import { Step } from '~components/wizard-form/props';
import ListAndConfirm from '~support-at-home/components/budgets/bulk-copy/ListAndConfirm';
import PreviewPanel from '~support-at-home/components/budgets/bulk-copy/PreviewPanel';
import PricePlanPicker from '~support-at-home/components/budgets/bulk-copy/PricePlanPicker';
import Review from '~support-at-home/components/budgets/bulk-copy/Review';

import { getAll as getClients } from '~main/actions/clients';
import { getAll as getStatuses } from '~support-at-home/actions/clientBudgetStatuses';
import { getAll as getFundingTypes } from '~support-at-home/actions/fundingTypes';

import { selectLookupClients } from '~main/selectors/clients/clients';
import { selectClientBudgetStatuses } from '~support-at-home/selectors/clientBudgetStatuses';
import { selectLookupFundingTypes } from '~support-at-home/selectors/fundingTypes';

const bulkCopyFields: Step[] = [
  {
    step: 1,
    title: 'Criteria',
    fields: {
      date_range: {
        type: 'date-range',
        field: 'date_range',
        required: true,
        caption: 'Date Range',
        description: 'Select the date range for the END date of the budgets to be copied.',
        validate: (values: any) =>
          validateDateRange({ start_date: values.date_range?.gte, end_date: values.date_range?.lte }),
      },
      status_ids: {
        type: 'multi-lookup',
        field: 'status_ids',
        caption: 'Budget Status',
        selectorOptions: selectClientBudgetStatuses,
        selectorActions: getStatuses,
      },
      funding_type_ids: {
        type: 'multi-lookup',
        field: 'funding_type_ids',
        caption: 'Funding Type',
        selectorOptions: selectLookupFundingTypes,
        selectorActions: getFundingTypes,
      },
      client_ids: {
        type: 'multi-lookup',
        field: 'client_ids',
        caption: 'Clients',
        selectorOptions: selectLookupClients,
        selectorActions: getClients,
      },
    },
  },
  {
    step: 2,
    title: 'Confirm Budgets to Copy',
    fields: {
      // Need to pass found budgets to field value to then use in step 3
      budget_ids: {
        type: 'custom',
        component: ListAndConfirm,
        dependencies: ['date_range', 'funding_type_ids', 'client_ids', 'status_ids'],
        colSize: 12,
      },
      start_date: {
        type: 'date',
        field: 'start_date',
        caption: 'New Start Date',
        required: true,
        show: (values) => {
          return values.budget_ids && values.budget_ids.length > 0;
        },
      },
      end_date: {
        type: 'date',
        field: 'end_date',
        caption: 'New End Date',
        required: true,
        show: (values) => {
          return values.budget_ids && values.budget_ids.length > 0;
        },
        validate: (values) => validateDateRange({ start_date: values.start_date, end_date: values.end_date }),
      },
      price_plan_change_warning: {
        type: 'alert-warning',
        colSize: 12,
        show: (values) => values.budget_ids && values.budget_ids.length > 0,
        deriveValue: () =>
          `Changing the price plan may result in some services being removed from copied budgets - You'll be able to review affected budgets before and after copying`,
      },
      price_plan: {
        type: 'custom',
        caption: 'Price Plan',
        component: PricePlanPicker,
        dependencies: ['price_plan_id', 'price_plan_revision_id', 'start_date', 'end_date'],
        isAttribute: false,
        show: (values) => values.budget_ids && values.budget_ids.length > 0,
      },
    },
    nextButtonCaption: 'Next',
    showNextButton: ({ values }) => {
      // Only allow next step if there are budgets to copy
      const budgetsData = values?.budget_ids;
      return budgetsData?.length > 0;
    },
  },
  {
    step: 3,
    title: 'Preview',
    fields: {
      preview: {
        type: 'custom',
        component: PreviewPanel,
        dependencies: ['budget_ids', 'start_date', 'end_date', 'price_plan_revision_id'],
        isAttribute: false,
        colSize: 12,
      },
    },
    nextButtonCaption: 'Confirm Copy',
    // Keep Confirm Copy disabled until the dry-run preview settles successfully (PreviewPanel
    // sets preview_ready). Prevents advancing into the real copy while the preview is loading/errored.
    // Also disabled when the preview found nothing copyable (every selected budget is un-copyable — e.g.
    // none of their packages resolve to a funding source), since the copy would create nothing.
    disableNext: ({ values }) => !values?.preview_ready || values?.has_copyable_budgets === false,
  },
  {
    step: 4,
    title: 'Review',
    fields: {
      copy_progress: {
        type: 'custom',
        component: Review,
        dependencies: ['budget_ids', 'start_date', 'end_date', 'price_plan_revision_id'],
        colSize: 12,
      },
    },
    showPrevButton: false,
    // Hide the footer Close (submit) button while the copy is running — Review publishes copy_in_progress
    // to Formik. It reappears once the run completes/fails so the user can close.
    showNextButton: ({ values }) => !values?.copy_in_progress,
  },
];

export default bulkCopyFields;
